import { FC, ReactNode, useMemo } from 'react';

import { useTranslation } from '@mopla/i18';

import { TRouteInputValue } from '../types';

interface IWithAddressLinesProps {
	value: TRouteInputValue;
	children: (lines: { primary: string; secondary: string }) => ReactNode;
}

export const WithAddressLines: FC<IWithAddressLinesProps> = ({
	value,
	children,
}) => {
	const { t } = useTranslation('searchForm');

	const addressLines = useMemo(() => {
		const { address, title, type, isCurrentLocation } = value;

		if (!address) {
			return { primary: title, secondary: '' };
		}

		const streetWithNumber = [address.street, address.houseNumber]
			.filter(Boolean)
			.join(' ');
		const postalCity = [address.postalCode, address.city]
			.filter(Boolean)
			.join(' ');

		/** Station (railway or stop): title is primary, street is secondary */
		if (type === 'railway' || type === 'stop') {
			return {
				primary: title,
				secondary: streetWithNumber,
			};
		}

		/** Current location: "Standort: street number" as primary, PLZ only as secondary */
		if (isCurrentLocation) {
			const locationLabel = streetWithNumber
				? t('rideHailingV2.currentLocationLabel', {
						street: streetWithNumber,
				  })
				: title;
			return {
				primary: locationLabel,
				secondary: address.postalCode || '',
			};
		}

		/** Regular address: "street number" as primary, "PLZ city" as secondary */
		return {
			primary: streetWithNumber || title,
			secondary: postalCity,
		};
	}, [value, t]);

	return <>{children(addressLines)}</>;
};
